import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import {
  Building2, Users, FileCheck, Shield, Loader2, ChevronDown, ChevronUp,
  Check, X, Palette, Link2, UserCheck, Clock, AlertTriangle, Briefcase
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Badge = ({ type }) => {
  const styles = {
    contracted: "bg-[rgba(0,200,224,0.15)] text-[#00C8E0] border-[rgba(0,200,224,0.3)]",
    subscriber: "bg-[rgba(139,92,246,0.15)] text-[#A78BFA] border-[rgba(139,92,246,0.3)]",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded text-xs font-medium border ${styles[type] || styles.subscriber}`} data-testid={`badge-${type}`}>
      {type === "contracted" ? "Contracted" : "Subscriber"}
    </span>
  );
};

const ClientTypesPage = () => {
  const [orgs, setOrgs] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [tab, setTab] = useState("organizations");
  const [savingId, setSavingId] = useState(null);
  const [brandingForm, setBrandingForm] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, approvalsRes, summaryRes] = await Promise.all([
        fetch(`${API}/api/client-types/organizations`, { credentials: "include" }),
        fetch(`${API}/api/client-types/subscriber-approvals?status=pending`, { credentials: "include" }),
        fetch(`${API}/api/client-types/summary`, { credentials: "include" }),
      ]);
      if (orgsRes.ok) { const d = await orgsRes.json(); setOrgs(d.organizations || []); }
      if (approvalsRes.ok) { const d = await approvalsRes.json(); setApprovals(d.approvals || []); }
      if (summaryRes.ok) { const d = await summaryRes.json(); setSummary(d); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setClientType = async (orgId, clientType) => {
    setSavingId(orgId);
    try {
      await fetch(`${API}/api/client-types/organizations/${orgId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_type: clientType }),
      });
      await fetchData();
    } catch (e) { console.error(e); }
    setSavingId(null);
  };

  const handleApproval = async (approvalId, decision) => {
    setSavingId(approvalId);
    try {
      await fetch(`${API}/api/client-types/subscriber-approvals/${approvalId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      await fetchData();
    } catch (e) { console.error(e); }
    setSavingId(null);
  };

  const saveBranding = async (orgId) => {
    setSavingId(orgId);
    try {
      await fetch(`${API}/api/client-types/organizations/${orgId}/branding`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(brandingForm),
      });
      await fetchData();
    } catch (e) { console.error(e); }
    setSavingId(null);
  };

  const toggleExpand = (orgId, branding) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      setBrandingForm(branding || {
        logo_url: "", primary_color: "#0A0F1C",
        company_name_display: "", use_client_templates: false, connector_access: true,
      });
    }
  };

  return (
    <QMSLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="client-types-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Building2 size={24} className="text-[#00C8E0]" />
              Client Type Management
            </h1>
            <p className="text-[#8B95A5] text-sm mt-1">
              Classify organizations as Contracted or Subscriber and manage their access
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#0F1A2E] border border-[rgba(255,255,255,0.06)] rounded-lg p-4" data-testid="stat-contracted">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[rgba(0,200,224,0.1)] flex items-center justify-center">
                  <Briefcase size={20} className="text-[#00C8E0]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{summary.contracted}</p>
                  <p className="text-[#8B95A5] text-xs">Contracted</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0F1A2E] border border-[rgba(255,255,255,0.06)] rounded-lg p-4" data-testid="stat-subscriber">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[rgba(139,92,246,0.1)] flex items-center justify-center">
                  <Users size={20} className="text-[#A78BFA]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{summary.subscriber}</p>
                  <p className="text-[#8B95A5] text-xs">Subscriber</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0F1A2E] border border-[rgba(255,255,255,0.06)] rounded-lg p-4" data-testid="stat-pending">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[rgba(232,168,56,0.1)] flex items-center justify-center">
                  <Clock size={20} className="text-[#E8A838]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{summary.pending_approvals}</p>
                  <p className="text-[#8B95A5] text-xs">Pending Approvals</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[rgba(255,255,255,0.06)]">
          {[
            { id: "organizations", label: "Organizations", icon: Building2 },
            { id: "approvals", label: `Approvals${approvals.length ? ` (${approvals.length})` : ""}`, icon: UserCheck },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === id ? "border-[#00C8E0] text-[#00C8E0]" : "border-transparent text-[#8B95A5] hover:text-white"
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-[#00C8E0]" />
          </div>
        ) : tab === "organizations" ? (
          /* Organizations Tab */
          <div className="space-y-3" data-testid="org-list">
            {orgs.length === 0 ? (
              <div className="text-center py-12 text-[#8B95A5]">
                <Building2 size={40} className="mx-auto mb-3 opacity-30" />
                <p>No organizations found. Add organizations via the CRM module.</p>
              </div>
            ) : (
              orgs.map((org) => (
                <div key={org.org_id} className="bg-[#0F1A2E] border border-[rgba(255,255,255,0.06)] rounded-lg overflow-hidden" data-testid={`org-row-${org.org_id}`}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[rgba(255,255,255,0.04)] flex items-center justify-center text-white font-bold text-sm">
                        {(org.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{org.name}</h3>
                        <p className="text-[#8B95A5] text-xs">{org.industry || "—"} · {(org.regions || []).join(", ") || "Global"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge type={org.client_type || "subscriber"} />
                      <div className="flex gap-1">
                        <button
                          onClick={() => setClientType(org.org_id, "contracted")}
                          disabled={savingId === org.org_id || org.client_type === "contracted"}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                            org.client_type === "contracted"
                              ? "bg-[rgba(0,200,224,0.2)] text-[#00C8E0] cursor-default"
                              : "bg-[rgba(255,255,255,0.04)] text-[#8B95A5] hover:bg-[rgba(0,200,224,0.1)] hover:text-[#00C8E0]"
                          }`}
                          data-testid={`set-contracted-${org.org_id}`}
                        >
                          {savingId === org.org_id ? <Loader2 size={12} className="animate-spin" /> : "Contracted"}
                        </button>
                        <button
                          onClick={() => setClientType(org.org_id, "subscriber")}
                          disabled={savingId === org.org_id || org.client_type === "subscriber"}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                            org.client_type === "subscriber" || !org.client_type
                              ? "bg-[rgba(139,92,246,0.2)] text-[#A78BFA] cursor-default"
                              : "bg-[rgba(255,255,255,0.04)] text-[#8B95A5] hover:bg-[rgba(139,92,246,0.1)] hover:text-[#A78BFA]"
                          }`}
                          data-testid={`set-subscriber-${org.org_id}`}
                        >
                          Subscriber
                        </button>
                      </div>
                      {org.client_type === "contracted" && (
                        <button
                          onClick={() => toggleExpand(org.org_id, org.branding)}
                          className="p-1.5 rounded text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                          data-testid={`expand-branding-${org.org_id}`}
                        >
                          {expandedOrg === org.org_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Branding Panel for Contracted */}
                  {expandedOrg === org.org_id && org.client_type === "contracted" && (
                    <div className="border-t border-[rgba(255,255,255,0.06)] p-4 bg-[rgba(0,200,224,0.02)]" data-testid={`branding-panel-${org.org_id}`}>
                      <h4 className="text-white text-sm font-medium flex items-center gap-2 mb-4">
                        <Palette size={16} className="text-[#00C8E0]" />
                        Branding & Customization
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-[#8B95A5] text-xs block mb-1">Company Display Name</label>
                          <input
                            value={brandingForm.company_name_display || ""}
                            onChange={(e) => setBrandingForm(f => ({ ...f, company_name_display: e.target.value }))}
                            className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-white text-sm outline-none focus:border-[#00C8E0]"
                            data-testid="branding-company-name"
                          />
                        </div>
                        <div>
                          <label className="text-[#8B95A5] text-xs block mb-1">Logo URL</label>
                          <input
                            value={brandingForm.logo_url || ""}
                            onChange={(e) => setBrandingForm(f => ({ ...f, logo_url: e.target.value }))}
                            placeholder="https://..."
                            className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-white text-sm outline-none focus:border-[#00C8E0]"
                            data-testid="branding-logo-url"
                          />
                        </div>
                        <div>
                          <label className="text-[#8B95A5] text-xs block mb-1">Primary Color</label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="color"
                              value={brandingForm.primary_color || "#0A0F1C"}
                              onChange={(e) => setBrandingForm(f => ({ ...f, primary_color: e.target.value }))}
                              className="w-10 h-10 rounded cursor-pointer border-0"
                              data-testid="branding-color"
                            />
                            <span className="text-[#8B95A5] text-xs">{brandingForm.primary_color || "#0A0F1C"}</span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={brandingForm.use_client_templates || false}
                              onChange={(e) => setBrandingForm(f => ({ ...f, use_client_templates: e.target.checked }))}
                              className="rounded"
                              data-testid="branding-templates"
                            />
                            <span className="text-white text-sm">Use client document templates</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={brandingForm.connector_access !== false}
                              onChange={(e) => setBrandingForm(f => ({ ...f, connector_access: e.target.checked }))}
                              className="rounded"
                              data-testid="branding-connectors"
                            />
                            <span className="text-white text-sm">ERP / QMS connector access</span>
                          </label>
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => saveBranding(org.org_id)}
                          disabled={savingId === org.org_id}
                          className="px-4 py-2 rounded bg-[#00C8E0] text-[#0A0F1C] text-sm font-medium hover:bg-[#00B4CC] transition-colors disabled:opacity-50"
                          data-testid={`save-branding-${org.org_id}`}
                        >
                          {savingId === org.org_id ? <Loader2 size={14} className="animate-spin" /> : "Save Branding"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Type Legend */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-[#0F1A2E] border border-[rgba(0,200,224,0.15)] rounded-lg p-4">
                <h4 className="text-[#00C8E0] text-sm font-medium flex items-center gap-2 mb-2">
                  <Briefcase size={16} /> Contracted
                </h4>
                <ul className="text-[#8B95A5] text-xs space-y-1">
                  <li className="flex items-center gap-1.5"><Check size={12} className="text-[#34D399]" /> Custom branding on documents (logo, colors)</li>
                  <li className="flex items-center gap-1.5"><Check size={12} className="text-[#34D399]" /> Use client's own templates</li>
                  <li className="flex items-center gap-1.5"><Check size={12} className="text-[#34D399]" /> ERP / QMS connector integration</li>
                  <li className="flex items-center gap-1.5"><Check size={12} className="text-[#34D399]" /> Dedicated customization</li>
                </ul>
              </div>
              <div className="bg-[#0F1A2E] border border-[rgba(139,92,246,0.15)] rounded-lg p-4">
                <h4 className="text-[#A78BFA] text-sm font-medium flex items-center gap-2 mb-2">
                  <Users size={16} /> Subscriber
                </h4>
                <ul className="text-[#8B95A5] text-xs space-y-1">
                  <li className="flex items-center gap-1.5"><Shield size={12} className="text-[#A78BFA]" /> IT approval routing required</li>
                  <li className="flex items-center gap-1.5"><Shield size={12} className="text-[#A78BFA]" /> Manager & admin verification</li>
                  <li className="flex items-center gap-1.5"><Shield size={12} className="text-[#A78BFA]" /> Prevents abandoned accounts</li>
                  <li className="flex items-center gap-1.5"><Shield size={12} className="text-[#A78BFA]" /> Standard CHARSTAN templates</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          /* Approvals Tab */
          <div className="space-y-3" data-testid="approvals-list">
            {approvals.length === 0 ? (
              <div className="text-center py-12 text-[#8B95A5]">
                <UserCheck size={40} className="mx-auto mb-3 opacity-30" />
                <p>No pending subscriber approvals</p>
              </div>
            ) : (
              approvals.map((a) => (
                <div key={a.approval_id} className="bg-[#0F1A2E] border border-[rgba(232,168,56,0.15)] rounded-lg p-4" data-testid={`approval-${a.approval_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-medium">{a.name || a.email}</h3>
                      <p className="text-[#8B95A5] text-xs mt-0.5">{a.email} · {a.company || "No company"}</p>
                      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                        <span className="text-[#8B95A5]">Manager: <span className="text-white">{a.direct_manager_name}</span></span>
                        <span className="text-[#8B95A5]">Manager Email: <span className="text-white">{a.direct_manager_email}</span></span>
                        <span className="text-[#8B95A5]">IT Admin: <span className="text-white">{a.it_admin_email}</span></span>
                        {a.department && <span className="text-[#8B95A5]">Dept: <span className="text-white">{a.department}</span></span>}
                      </div>
                      {a.justification && (
                        <p className="text-[#8B95A5] text-xs mt-2 italic">"{a.justification}"</p>
                      )}
                      <p className="text-[#555] text-xs mt-2">{a.approval_id} · Requested {new Date(a.requested_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproval(a.approval_id, "approved")}
                        disabled={savingId === a.approval_id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[rgba(52,211,153,0.1)] text-[#34D399] text-xs font-medium hover:bg-[rgba(52,211,153,0.2)] transition-colors"
                        data-testid={`approve-${a.approval_id}`}
                      >
                        <Check size={14} /> Approve
                      </button>
                      <button
                        onClick={() => handleApproval(a.approval_id, "rejected")}
                        disabled={savingId === a.approval_id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[rgba(239,68,68,0.1)] text-[#EF4444] text-xs font-medium hover:bg-[rgba(239,68,68,0.2)] transition-colors"
                        data-testid={`reject-${a.approval_id}`}
                      >
                        <X size={14} /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default ClientTypesPage;
